/* eslint-disable max-lines-per-function */
import {html, nothing} from 'lit';
import {createRef, ref} from 'lit/directives/ref.js';

/** @typedef {new (...args: any[]) => any} Constructor */

/**
 * @template {!Constructor} T
 * @param {T} superClass - The class to extend
 */
export const FormElementMixin = superClass => class FormElement extends superClass {
	static formAssociated = true;

	static get properties () {
		return {
			_value: {
				attribute: 'value',
				type: String
			}
		};
	}

	/**
	 * @type {import('lit/directives/ref.js').Ref<HTMLFormElement>}
	 */
	formRef = createRef();

	// Expose ElementInternals properties to prototype

	/**
	 * @type {HTMLFormElement}
	 */
	get form () {
		return this._internals.form;
	}

	/**
	 * @type {NodeList}
	 */
	get labels () {
		return this._internals.labels;
	}

	/**
	 * @type {ValidityStateFlags}
	 */
	get validity () {
		for (let formControl of this.formRef?.value?.elements ?? []) {
			for (let key in formControl?.validity ?? {}) {
				if (key !== 'valid' && formControl?.validity?.[key]) {
					return formControl?.validity;
				}
			}
		}

		return {};
	}

	/**
	 * @type {string}
	 */
	get validationMessage () {
		for (let formControl of this.formRef?.value?.elements ?? []) {
			if (formControl?.validationMessage !== '') {
				return formControl.validationMessage;
			}
		}

		return '';
	}

	/**
	 * @type {Element}
	 */
	get validationAnchor () {
		for (let formControl of this.formRef?.value?.elements ?? []) {
			if (formControl?.validationMessage !== '') {
				return formControl?.validationAnchor || formControl;
			}
		}

		return void 0;
	}

	/**
	 * @type {FormData}
	 */
	get value () {
		return this.formRef?.value
			? new FormData(this.formRef?.value)
			: void 0;
	}

	/**
	 * @type {FormData | Map}
	 */
	set value (newValue) {
		if (newValue instanceof FormData || newValue instanceof Map) {
			// eslint-disable-next-line array-element-newline
			for (let [key, value] of newValue?.entries()) {
				const element = this.formRef?.value?.elements?.namedItem(key);

				if (element.type === 'checkbox') {
					element.checked = value;
				}
				else {
					element.value = value;
				}
			}
		}

		throw new Error('You must pass a FormData or Map instance in order to set form values.');
	}

	/**
	 * @param  {...any} args
	 */
	constructor (...args) {
		super(...args);

		/**
		 * @type {ElementInternals}
		 */
		this._internals = this.attachInternals();

		/**
		 * @type {string}
		 */
		this._value = '';
	}

	checkValidity () {
		return this._internals?.checkValidity() ?? false;
	}

	reportValidity () {
		return this._internals?.reportValidity() ?? false;
	}

	updateElementInternals () {
		this._internals.setFormValue(this.value);
		this._internals.setValidity(this.validity, this.validationMessage, this.validationAnchor);
	}

	firstUpdated () {
		this._internals.setFormValue(this.value);
	}

	async updated () {
		await this.updateComplete;

		this.updateElementInternals();

		this.dispatchEvent(new CustomEvent('change', {
			bubbles: true,
			composed: true
		}));
	}

	/**
	 * @param {Event} e
	 */
	handleFormSubmit (e) {
		e.preventDefault();

		this.updateElementInternals();

		this.dispatchEvent(new CustomEvent('submit', {
			bubbles: true,
			composed: true
		}));
	}

	/**
	 * @param {Event} e
	 */
	handleFormChange () {
		this.updateElementInternals();

		this.dispatchEvent(new CustomEvent('change', {
			bubbles: true,
			composed: true
		}));
	}

	renderInnerForm () {
		return nothing;
	}

	render () {
		return html`<form ${ref(this.formRef)} @submit="${this.handleFormSubmit}" @change="${this.handleFormChange}">
			${this.renderInnerForm()}
		</form>`;
	}
};
